<script setup>
import { FormInput } from "@Components/Form";
import { PrimaryButton } from "@Components/Button";
import { login } from "@/auth";
import { useForm } from "@/form";
import { useI18n } from "vue-i18n";
import AppLogo from "@Components/AppLogo.vue";
import { Modal } from "@Components/Modal";
import { ref, onMounted, onUnmounted, getCurrentInstance } from "vue";
import AppLink from "@Components/AppLink.vue";
import { OtomaticAIIcon } from "@Icons/index";

const { t: __ } = useI18n();

const form = useForm({
    email: "",
    password: "",
});

const quotaExceededModalRef = ref(false);
const subscriptionRequiredModalRef = ref(false);
const popupRef = ref(null);

const saasUrl = getCurrentInstance().appContext.config.globalProperties.$app.saas_url;

const openAuthorizeUrl = async () => {
    const data = await useForm().post("auth_oauth_authorize");
    const url = data.url;
    popupRef.value = window.open(url, "otomatic_oauth", "width=480,height=640");
};

const submit = async () => {
    if (!form.processing) {
        try {
            await login(form);
            window.location.reload();
        } catch (e) {
            if (e.response?.data?.error === "Quota exceeded.") {
                quotaExceededModalRef.value.show();
            } else if (e.response?.data?.error === "Subscription required.") {
                subscriptionRequiredModalRef.value.show();
            }
        }
    }
};

const messageListener = async (event) => {
    if (event.origin !== saasUrl) return;

    const data = event.data || {};
    if (data.type !== "otomatic_oauth_done") return;

    if (data.error) {
        if (popupRef.value && !popupRef.value.closed) popupRef.value.close();
        return;
    }

    // Échange du code côté serveur WP (avec PKCE)
    try {
        const res = await useForm({
            code: data.code,
            state: data.state,
        }).post("auth_oauth_complete");

        if (popupRef.value && !popupRef.value.closed) popupRef.value.close();

        location.reload();
    } catch (e) {
        if (e.response?.data?.error === "Quota exceeded.") {
            quotaExceededModalRef.value.show();
        } else if (e.response?.data?.error === "Subscription required.") {
            subscriptionRequiredModalRef.value.show();
        }

        if (popupRef.value && !popupRef.value.closed) popupRef.value.close();
    }
};

onMounted(() => {
    window.addEventListener("message", messageListener);
});

onUnmounted(() => {
    window.removeEventListener("message", messageListener);
});
</script>

<template>
    <div class="min-h-full flex flex-col flex-1 justify-center px-6 py-12 lg:px-8">
        <div class="sm:mx-auto sm:w-full sm:max-w-sm">
            <div class="flex justify-center">
                <AppLogo />
            </div>
            <h2 class="mt-10 text-2xl font-bold tracking-tight leading-9 text-center text-gray-900 dark:text-white">
                {{ __("Connectez-vous à votre compte") }}
            </h2>
        </div>

        <div class="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
            <!-- <form class="space-y-6" @submit.prevent="submit">
                <FormInput v-model="form.email" :label="__('E-mail')" required type="email" autocomplete="email" :errors="form.errors.get('email')" />
                <FormInput
                    v-model="form.password"
                    id="password"
                    required
                    type="password"
                    autocomplete="current-password"
                    :label="__('Mot de passe')"
                    :errors="form.errors.get('password')" />

                <div>
                    <PrimaryButton :processing="form.processing" type="submit" class="w-full justify-center leading-6">{{ __("Se connecter") }}</PrimaryButton>
                </div>
            </form> -->

            <div class="flex justify-center">
                <PrimaryButton :icon="OtomaticAIIcon" @click="openAuthorizeUrl"> {{ __("Se connecter avec Otomatic") }} </PrimaryButton>
            </div>

            <p class="mt-10 text-sm text-center text-gray-600 dark:text-gray-400">
                {{ __("Pas de compte ?") }}
                {{ " " }}
                <a href="https://studio.otomatic.ai/register" target="_blank" class="font-semibold leading-6 text-gray-900 dark:text-white hover:underline">{{
                    __("Créez un compte gratuitement")
                }}</a>
            </p>
        </div>

        <Modal ref="quotaExceededModalRef">
            <div class="p-6">
                <div class="text-lg font-medium text-center text-gray-900 dark:text-white">
                    {{ __("Votre quota de nombre de domaines a été dépassé.") }}
                </div>
                <div class="mt-3 text-sm text-center text-gray-500 dark:text-gray-400">
                    {{ __("Souscrivez à un plan premium pour augmenter votre quota.") }}
                </div>
                <div class="flex justify-center items-center mt-3">
                    <PrimaryButton :as="AppLink" to="https://studio.otomatic.ai/user/billing" target="_blank" @click="quotaExceededModalRef.hide()"
                        >{{ __("Modifier mon plan") }}
                    </PrimaryButton>
                </div>
                <div class="mt-8 text-sm text-center text-gray-500 dark:text-gray-400">
                    {{ __("Vous pouvez également supprimer un domaine pour libérer de la place.") }}
                </div>
                <div class="flex justify-center items-center mt-3">
                    <PrimaryButton :as="AppLink" to="https://studio.otomatic.ai/domains" target="_blank" @click="quotaExceededModalRef.hide()">{{
                        __("Supprimer un domaine")
                    }}</PrimaryButton>
                </div>
            </div>
        </Modal>

        <Modal ref="subscriptionRequiredModalRef">
            <div class="p-6">
                <div class="text-lg font-medium text-center text-gray-900 dark:text-white">
                    {{ __("Vous devez souscrire à un plan premium pour continuer.") }}
                </div>
                <div class="mt-3 text-sm text-center text-gray-500 dark:text-gray-400">
                    {{ __("Souscrivez à un plan premium pour continuer.") }}
                </div>
                <div class="flex justify-center items-center mt-3">
                    <PrimaryButton :as="AppLink" to="https://studio.otomatic.ai/user/billing" target="_blank" @click="subscriptionRequiredModalRef.hide()"
                        >{{ __("Souscrire à un plan premium") }}
                    </PrimaryButton>
                </div>
            </div>
        </Modal>
    </div>
</template>
